import Layout from "@/screens/Layout";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useMemo } from "react";  

// import "../component/ExitPollRegionalV2/index.css";
import { ampGlobalStyles } from "@/component/Amp/styles";
import useIsMobile from "@/hooks/useIsMobile";
import sendGAEvent from "@/utils/gaEvent";
import { MasterConfigContext } from "@/context/MasterConfigContext";
import { AuthProvider } from "@/context/AuthContext";
import { GoldSilverProvider } from "@/context/GoldSilverContext";

 
const ComscoreScript = dynamic(() => import("@/component/Ads/ComscoreScript"));
const ChartbeatScript = dynamic(() => import("@/component/Ads/ChartbeatScript"));
const OpenWrapScript = dynamic(() => import("@/component/Ads/OpenWrapScript"));
const AmazonAds = dynamic(() => import("@/component/Ads/AmazonAds"));
const VDOAdsScript = dynamic(() => import("@/component/Ads/VDOAdsScript"));
const AmpLayout = dynamic(() => import("@/screens/Layout/AmpLayout"));
const SiteNavigationSchema = dynamic(() =>
  import("@/component/SeoTags/SiteNavigationSchema")
);
const OrganisationSchema = dynamic(() =>
  import("@/component/SeoTags/OrganisationSchema")
);
const WebsiteSchema = dynamic(() =>
  import("@/component/SeoTags/WebsiteSchema")
);
const MagniteAnalytics = dynamic(() => import("@/component/Ads/MagniteScript"));
const NewzoTrackingScript = dynamic(() =>
  import("@/component/Ads/NewzoTrackingScript")
);

 let _menuCache = null;

function App({ Component, masterConfigData, ...props }) {
  const router = useRouter();
  const isAmp = router?.asPath?.startsWith("/amp");
  const isAmpMin = router?.asPath?.startsWith("/amp/xiaomi");
  const isShareRoute = router?.asPath?.startsWith("/share/");
  const isPhotoGallery = router?.asPath?.includes("/photo/");
  const isMobileDevice = useIsMobile();

   
  const isElectionRoute = useMemo(
    () =>
      router?.asPath === "/elections/lok-sabha-elections-2024/results" ||
      router?.asPath.includes("/haryana-nikay-chunav") ||
      router?.asPath.includes("/board-results") ||
      router?.asPath.includes("india-tour-of-england") ||
      (router?.asPath.includes("/cricket") &&
        router?.asPath.includes("-vs-")) ||
      router?.asPath.includes("/ipl") ||
      router?.asPath === "/elections/lok-sabha-elections-2024/schedule" ||
      router?.asPath === "/elections/lok-sabha-elections-2024" ||
      router?.asPath?.includes("/elections/haryana-assembly") ||
      router?.asPath?.includes("/elections/maharashtra-assembly") ||
      router?.asPath?.includes("/elections/jharkhand-assembly") ||
      router?.asPath?.includes("/elections/delhi-assembly") ||
      router?.asPath ===
        "/elections/lok-sabha-elections-2024/constituency-list" ||
      router?.asPath === "/elections/faqs" ||
      (router?.asPath?.includes("/elections/lok-sabha-elections-2024") &&
        router?.asPath?.includes("-constituency-result-")) ||
      router?.asPath?.includes("/elections/assembly-chunav") ||
      router?.asPath?.includes("/madhya-pradesh/mptak-baithak") ||
      router?.asPath?.includes("/bihar/bihar-tak-baithak") ||
      router?.asPath?.includes("-livetv") ||
      router?.asPath?.includes("-gold-price-today") ||
      router?.asPath?.includes("-silver-price-today"),
    [router?.asPath]
  );

  useEffect(() => {
    
    const updateFavicon = () => {
      let faviconUrl = "/favicon.ico";
      if (
        router.asPath.includes("/rajasthan") ||
        router.asPath.includes("/amp/rajasthan")
      ) {
        faviconUrl = "/favicon_rj.ico";
      } else if (
        router.asPath.includes("/madhya-pradesh") ||
        router.asPath.includes("/amp/madhya-pradesh")
      ) {
        faviconUrl = "/favicon_mp.ico";
      } else if (
        router.asPath.includes("/bihar") ||
        router.asPath.includes("/amp/bihar")
      ) {
        faviconUrl = "/favicon_bihar.ico";
      } else if (
        router.asPath.includes("/haryana") ||
        router.asPath.includes("/amp/haryana")
      ) {
        faviconUrl = "/favicon_haryana.ico";
      } else if (
        router.asPath.includes("/chhattisgarh") ||
        router.asPath.includes("/amp/chhattisgarh")
      ) {
        faviconUrl = "/ccg_favicon.ico";
      }
      const link = document.querySelector("link[rel='icon']");
      if (link) link.href = faviconUrl;
    };

    updateFavicon();

    async function onFirstScroll() {
      window._izq = window._izq || [];
      window._izq.push(["init"]);
      const izooto = document.createElement("script");
      izooto.setAttribute(
        "src",
        "https://cdn.izooto.com/scripts/c4b2e3a7dec48a6951b66a68aa8641118c2e6502.js"
      );
      izooto.setAttribute("async", "true");
      document.head.appendChild(izooto);
    }

    function handleScroll() {
      onFirstScroll();
      window.removeEventListener("scroll", handleScroll);
    }

    window.addEventListener("scroll", handleScroll);

    const isFirstTimeVisit = localStorage?.getItem("first_visit");
    if (!isFirstTimeVisit) {
      sendGAEvent({
        eventName: "first_visit",
        pageLocation: window?.location?.href,
        pageTitle: document?.title,
      });
      localStorage.setItem("first_visit", true);
    }
  }, []);

  useEffect(() => {

    const loadGaScript = (scriptId, codeId, gaCode) => {
      if (document.getElementById(scriptId)) return;

      const script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaCode}`;
      script.async = true;
      script.onload = () => {
        const gaCodeScript = document.createElement("script");
        gaCodeScript.id = codeId;
        gaCodeScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${gaCode}');
        `;
        document.head.appendChild(gaCodeScript);
      };
      document.head.appendChild(script);
    };

    loadGaScript("gaScript", "gaCode", process.env.NEXT_PUBLIC_GACODE);
    loadGaScript("gaScriptNew", "gaCodeNew", process.env.NEXT_PUBLIC_GACODE_NEW);
  }, []);

  return isAmp ? (
    <>
      <style jsx global>
        {ampGlobalStyles}
      </style>
      {!isAmpMin && (
        <Head>
          <script
            async
            custom-element="amp-ad"
            src="https://cdn.ampproject.org/v0/amp-ad-0.1.js"
          ></script>
          <script
            async
            custom-element="amp-analytics"
            src="https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
          ></script>
          <script
            async
            custom-element="amp-sidebar"
            src="https://cdn.ampproject.org/v0/amp-sidebar-0.1.js"
          ></script>
          <script
            async
            custom-element="amp-sticky-ad"
            src="https://cdn.ampproject.org/v0/amp-sticky-ad-1.0.js"
          ></script>
          <script
            async
            custom-element="amp-accordion"
            src="https://cdn.ampproject.org/v0/amp-accordion-0.1.js"
          ></script>
        </Head>
      )}
      {isAmpMin && (
        <Head>
          <script
            async
            custom-element="amp-analytics"
            src="https://cdn.ampproject.org/v0/amp-analytics-0.1.js"
          ></script>
        </Head>
      )}
      {!isAmpMin && <SiteNavigationSchema data={props?.menu} />}
      <OrganisationSchema />
      <MasterConfigContext.Provider value={masterConfigData}>
        <AmpLayout menuData={props?.menu} isPhotoGallery={isPhotoGallery}>
          <amp-analytics
            config="https://www.googletagmanager.com/amp.json?id=GTM-T62NS66&gtm.url=SOURCE_URL"
            data-credentials="include"
          ></amp-analytics>
          <Component {...props.pageProps} />
        </AmpLayout>
      </MasterConfigContext.Provider>
    </>
  ) : isShareRoute ? (
    <MasterConfigContext.Provider value={masterConfigData}>
      <Component {...props.pageProps} />
    </MasterConfigContext.Provider>
  ) : (
    <>
      <style jsx global>{`
        html {
          font-family: "Mukta", "MuktaFallback", sans-serif;
          font-display: swap;
        }
      `}</style>

      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=5"
        />
        
      </Head>
      <SiteNavigationSchema data={props?.menu} />
      <OrganisationSchema />
      <WebsiteSchema />

      <MasterConfigContext.Provider value={masterConfigData}>
        <AuthProvider>
          <GoldSilverProvider>
          <Layout menuData={props?.menu} isMobileDevice={isMobileDevice}>
            <Component {...props.pageProps} />
          </Layout>
          </GoldSilverProvider>
        </AuthProvider>
      </MasterConfigContext.Provider>
      {<OpenWrapScript />}
      <MagniteAnalytics />
      <AmazonAds />
      <ComscoreScript />
      <ChartbeatScript />
      <NewzoTrackingScript />

      {!isElectionRoute && <VDOAdsScript />}
      {!isAmp && (
        <script
          defer
          src="https://akm-img-a-in.tosshub.com/sites/common/resources/ssoonetap/prod/js/min/onetapsdk.js?ver=1107202"
        ></script>
      )}
    </>
  );
}
const stripMenuItem = ({ active_svg, sub_menu, ...item }) => ({
  ...item,
  ...(sub_menu !== undefined && {
    sub_menu: Array.isArray(sub_menu) ? sub_menu.map(stripMenuItem) : sub_menu,
  }),
});
const trimMenu = (menu) =>
  menu && Array.isArray(menu.header_menu)
    ? { header_menu: menu.header_menu.map(stripMenuItem) }
    : menu;

App.getInitialProps = async (appCtx) => {
  const { logger } = await import("@/utils/logging/log-util");

  try {
    // Check if running on server
    let isServer = !!appCtx.ctx.req;

    let menu = null;
    let masterConfigData = null;
    if (isServer) {
      console.log("App.js running on the server");
      const PORT = process.env.PORT;
      try {
        const response = await fetch(`http://localhost:${PORT}/api/menu`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Network response was not ok");
      menu = trimMenu(await response.json());
      } catch (error) {
        console.error("Menu Fetch error", error);
      }
      try {
        const response = await fetch(`http://localhost:${PORT}/api/masterconfig`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }); 
        if (!response.ok) throw new Error("Network response was not ok");
        const result = await response.json();
        masterConfigData = result?.data;
      } catch (error) {
        console.error("MasterConfig Fetch error", error);
      }
    } else {
      console.log("Running on the client");
      if (_menuCache) {
        menu = _menuCache;
      } else {
        try {
          const response = await fetch(
            `${process.env.NEXT_PUBLIC_WEBSITE_URL}/api/menu`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          if (!response.ok) throw new Error("Network response was not ok");
          menu = trimMenu(await response.json());
          _menuCache = menu;
        } catch (error) {
          console.error("Menu Fetch error", error);
        }
      }
    }

    return {
      menu,
      masterConfigData,
    };
  } catch (error) {
    logger.error(error);
    return {
      menu: [],
    };
  }
};

export default App;
